import { useQuery, UseQueryResult } from '@tanstack/react-query';
import ky from 'ky';

import { pointsDomain } from '@/features/vaults/config';
import { VaultResponse } from '@/features/vaults/types';

/**
 * Get all vaults data and metadata.
 * NOTE: this is stale data, it's cached in the API.
 * Do not use for anything that requires up-to-date data.
 */
export function useVaultsData(
  options: { enabled: boolean; pointsDomain?: string; addresses?: string[] } = {
    enabled: true,
    pointsDomain,
  },
): UseQueryResult<VaultResponse[]> {
  const addresses = options.addresses;

  return useQuery({
    queryKey: ['vaults', 'data', addresses],
    queryFn: async ({ signal }) => {
      const searchParams = addresses?.length
        ? addresses.map(a => ['addresses', a] as [string, string])
        : undefined;

      const timeout = searchParams ? 5000 : 10000;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      // React Query cancels via its own signal — propagate to our controller
      signal?.addEventListener('abort', () => controller.abort());

      try {
        return (await ky(`${options.pointsDomain || pointsDomain}/v1/vaults`, {
          searchParams,
          signal: controller.signal,
        }).json()) as VaultResponse[];
      } finally {
        clearTimeout(timeoutId);
      }
    },
    staleTime: 10 * 60 * 1000,
    gcTime: Infinity,
    enabled: options.enabled,
    retry: 3,
  });
}
